import { type ICollectionDetails } from "~/hooks/useCollections"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import TopCustomersPieChartCard from "~/components/collections/cards/TopCustomersPieChartCard"
import CollectionPreOrdersGraphCard from "~/components/collections/cards/CollectionPreOrderGraphCard"
import { CustomerTable } from "~/components/reusable/CustomerTable"
import { useSkuStore } from "~/providers/sku-store-provider"
import { AnalysisPage } from "~/components/design-system/pages"
import ViewConfiguration from "~/components/planning/ViewConfiguration"
import CollectionInfoCard from "~/components/demand-plan/CollectionInfoCard"
import { CustomerAggregationOptions } from "~/components/reusable/CustomerAggregationOptions"
import { type PresetView } from "~/components/planning/presetView"
import { useDemandPlanContext } from "~/context/DemandPlanProvider"

type Props = {
  collections: ICollectionDetails[]
  variants: IVariantSummary[]
  startDate: Date
  endDate: Date
  presetViews?: PresetView[]
  presetGroupId?: string
}

const CollectionCustomersTab = ({
  collections,
  variants,
  startDate,
  endDate,
  presetViews,
  presetGroupId,
}: Props) => {
  const collectionIds = collections.map((collection) => collection.id)
  const { demandPlanId } = useDemandPlanContext()
  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)
  const selectedChannel = useSkuStore((state) => state.channel)

  const viewConfiguration = (
    <ViewConfiguration
      tableId="customers-collection"
      showFilters={false}
      showWarehouseSelect
      showChannelSelect
      extraDisplayOptions={<CustomerAggregationOptions tableId="customers-collection" />}
      presetViews={presetViews}
      presetGroupId={presetGroupId}
    />
  )

  const inspector = (
    <>
      <CollectionInfoCard
        collections={collections}
        variants={variants}
        defaultExpanded={false}
        demandPlanId={demandPlanId}
      />
      <TopCustomersPieChartCard
        collections={collections}
        startDate={startDate}
        endDate={endDate}
        warehouseId={selectedWarehouseId}
        channel={selectedChannel}
      />
      <CollectionPreOrdersGraphCard
        collections={collections}
        startDate={startDate}
        endDate={endDate}
        warehouseId={selectedWarehouseId}
        channel={selectedChannel}
      />
    </>
  )

  return (
    <AnalysisPage
      fillContent
      viewConfiguration={viewConfiguration}
      inspector={inspector}
      inspectorWidth="lg"
      inspectorClassName="[&>*]:shrink-0 h-full"
    >
      <CustomerTable collectionIds={collectionIds} id="customers-collection" />
    </AnalysisPage>
  )
}

export default CollectionCustomersTab
